import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Loader2, ScanSearch, FileText, Bot } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const INCLUDES = [[ScanSearch, 'Deep website & revenue-leak analysis'], [Bot, 'AI voice & chat demo agent'], [FileText, 'Proposal with live demos']];

export const DeepAnalysisDialog = ({ open, leadIds, skipped = 0, onClose, api, workspaceId, dark, onStarted }) => {
  const [config, setConfig] = useState(null);
  const [subId, setSubId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const request = useRef(null);
  const base = `/api/workspaces/${workspaceId}/lead-runs`;
  const count = leadIds.length;

  useEffect(() => {
    if (!open) return;
    setConfig(null); setError(''); request.current = null;
    api.get(`${base}/config`).then(({ data }) => {
      setConfig(data);
      if (data.sub_accounts.length === 1) setSubId(data.sub_accounts[0].sub_account_id);
    }).catch(() => setError('Could not load pricing. Please try again.'));
  }, [open, api, base]);

  const keyMode = config?.has_claude_key ? 'byok' : 'platform';
  const rate = config?.[keyMode === 'byok' ? 'byok_rate' : 'standard_rate'] || 0;
  const total = Number((count * rate).toFixed(2));
  const enough = config?.unlimited || config?.available_balance >= total;
  const blocked = busy || !config || !count || !subId || !enough;

  const start = async () => {
    setBusy(true); setError('');
    try {
      const payload = { count, city: '', industries: [], website: '', lead_ids: leadIds, sub_account_id: subId,
        key_mode: keyMode, consent: true, accepted_rate: rate, accepted_budget: total };
      if (!request.current) request.current = crypto.randomUUID();
      const { data } = await api.post(base, { ...payload, request_id: request.current });
      if (data.status === 'rejected') throw new Error('The charge was declined. Please check your balance.');
      onStarted?.(data);
    } catch (e) {
      request.current = null;
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : e.message || 'Could not start.');
    } finally { setBusy(false); }
  };

  const muted = dark ? 'text-white/50' : 'text-slate-500';
  const box = dark ? 'border-white/10 bg-white/[0.03]' : 'border-slate-200 bg-slate-50';
  return <Dialog open={open} onOpenChange={v => { if (!v && !busy) onClose(); }}>
    <DialogContent overlayClassName="z-[90] backdrop-blur-sm" className={`z-[95] w-[calc(100%-2rem)] max-w-xl p-6 sm:p-8 rounded-2xl ${dark ? 'bg-[#0f1115] border-white/10 text-white' : 'bg-white'}`} data-testid="deep-analysis-modal">
      <DialogHeader>
        <span className="w-11 h-11 rounded-xl bg-violet-500/15 text-violet-400 flex items-center justify-center mb-2"><Sparkles size={20} /></span>
        <DialogTitle className="text-2xl font-display font-black">Run Deep Analysis &amp; Proposal</DialogTitle>
        <DialogDescription className={muted}>Each business gets everything below, built in the background. Starts right away, or queues behind runs already in progress.</DialogDescription>
      </DialogHeader>
      <ul className="grid gap-2 mt-2">{INCLUDES.map(([Icon, t]) => <li key={t} className={`flex items-center gap-2.5 text-sm ${muted}`}><Icon size={15} className="text-teal-400" />{t}</li>)}</ul>
      <div className={`mt-5 rounded-2xl border p-5 flex items-end justify-between gap-4 ${box}`} data-testid="deep-analysis-quote">
        <div>
          <p className="text-4xl font-black leading-none" data-testid="deep-analysis-count">{count}</p>
          <p className={`text-xs mt-1.5 ${muted}`}>{count === 1 ? 'business' : 'businesses'} × ${rate.toFixed(2)}</p>
        </div>
        <div className="text-right">
          <p className={`text-[11px] uppercase tracking-widest font-bold ${muted}`}>Total</p>
          <p className="text-3xl font-black text-teal-400" data-testid="deep-analysis-total">{config ? `$${total.toFixed(2)}` : '—'}</p>
          {config && <p className={`text-[11px] mt-1 ${muted}`} data-testid="deep-analysis-balance">{config.unlimited ? 'Included with your plan' : `$${Number(config.available_balance).toFixed(2)} available`}</p>}
        </div>
      </div>
      {skipped > 0 && <p className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-300" data-testid="deep-analysis-skipped">
        {skipped} selected {skipped === 1 ? 'business has' : 'businesses have'} no website and will be skipped. Deep analysis needs a website to read.</p>}
      {config?.sub_accounts?.length > 1 && <label className={`block mt-4 text-xs font-semibold ${muted}`}>Create demo agents in
        <select data-testid="deep-analysis-subaccount" value={subId} onChange={e => setSubId(e.target.value)}
          className={`mt-1.5 w-full px-3 py-2.5 rounded-xl border text-sm ${dark ? 'bg-black/30 border-white/10 text-white' : 'bg-white border-slate-200'}`}>
          <option value="">Choose a sub-account</option>
          {config.sub_accounts.map(s => <option key={s.sub_account_id} value={s.sub_account_id}>{s.name}</option>)}
        </select></label>}
      {config && !config.sub_accounts?.length && <p className="mt-4 text-sm text-amber-400" data-testid="deep-analysis-no-sub">Create a sub-account first — demo agents are built inside it.</p>}
      <p className={`mt-4 text-xs ${muted}`}>Only completed businesses are charged. Anything that fails is refunded automatically.</p>
      {config && !enough && <p className="mt-2 text-sm text-red-400" role="alert" data-testid="deep-analysis-low-balance">Not enough balance. <Link className="underline" to={`/workspace/${workspaceId}/billing`}>Add funds</Link></p>}
      {error && <p className="mt-2 text-sm text-red-400" role="alert" data-testid="deep-analysis-error">{error}</p>}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <button onClick={onClose} disabled={busy} data-testid="deep-analysis-cancel"
          className={`py-3 rounded-xl border text-sm font-semibold transition-colors ${dark ? 'border-white/15 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'}`}>Cancel</button>
        <button onClick={start} disabled={blocked} data-testid="deep-analysis-confirm"
          className="py-3 rounded-xl bg-violet-500 text-white text-sm font-bold hover:bg-violet-400 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
          {busy || !config ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Confirm
        </button>
      </div>
    </DialogContent>
  </Dialog>;
};
